import { Suspense, useRef, useState, lazy } from 'react';
import Eyebrow from '../../../../components/blackrock/advisor-center/eyebrow/Eyebrow';
import Style from '../../../../components/blackrock/advisor-center/style/Style';
import { PollAnswers } from './components/PollAnswers';
import { useRecentlyLoaded } from './helpers/useRecentlyLoaded';
import { Answer, PollProps } from './Poll.model';
import PollCTAContainer from './components/PollCTAContainer';
import { isSsr } from '../../../../_helpers/isSsr';
import { useHydrated } from '../../../../_helpers/hooks/useHydrated';
import { handleSignOnRedirect } from '../../../../_helpers/handleSignOnRedirect';
import { useSimplePoll } from './helpers/useSimplePoll';
import { usePendingVoteDataLinkEventHandler } from '../../../../_helpers/hooks/usePendingDataLinkEventHandler';
import { isCwpUserSignedIn } from '../../../../_helpers/isCwpUserSignedIn';
import { useExpiryCounter } from './helpers/useExpiryCounter';
import {
	CookieCategory,
	createCookieToaster,
	isCookieAllowed,
	NO_COOKIES_ACCEPTED_ERROR_MSG,
	NO_COOKIES_ACCEPTED_STATUS_CODE,
} from '../../../../_helpers/manageCookies';
import SignInDialog, {
	SignInDialogRef,
} from '../../../../components/blackrock/advisor-center/sign-in-dialog/SignInDialog';
import { joinString } from '../../../../_helpers/join-string';
import { vote } from './Poll.service';

import './Poll.scss';

const PollIcon = lazy(() => import('./PollIcon'));

export const Poll: React.FC<PollProps> = ({
	poll,
	voteUrl,
	liveVoteCountFetchSeconds,
	simpleVoteCountUrl,
	fullWidthAnimation = false,
}: PollProps) => {
	const {
		id,
		eyebrow,
		question,
		answers,
		expiryDate,
		theme = 'dark',
		userVote,
		ctaContainers,
		pastPollResults,
		translationMap,
	} = poll;
	const translations = translationMap.poll;
	const [answersState, setAnswersState] = useState<Answer[]>(answers);
	const [answered, setAnswered] = useState(userVote?.toString() || '');
	const votes = answersState.reduce((prev, curr) => prev + curr.votes, 0);
	const expiryDateAsDate = new Date(expiryDate);
	const [expired, expiry] = useExpiryCounter(
		expiryDateAsDate,
		translations.timeSpan
	);
	const recentlyLoaded = useRecentlyLoaded();
	const signInDialogRef = useRef<SignInDialogRef>(null);
	if (!simpleVoteCountUrl) {
		console.error('simpleVoteCountUrl is required');
		return <></>;
	}
	useSimplePoll(
		simpleVoteCountUrl,
		liveVoteCountFetchSeconds,
		id,
		setAnswersState,
		expired
	);

	usePendingVoteDataLinkEventHandler(poll.pendingDataLinkEvent);

	const handleAnswerClicked = (answerIndex: number) => {
		if (!isCookieAllowed(CookieCategory.PREFERENCE)) {
			createCookieToaster(translationMap.cookieToaster);
			return;
		}
		if (!isCwpUserSignedIn()) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			signInDialogRef.current.open(() => handleAnswered(answerIndex));
			return;
		}
		handleAnswered(answerIndex);
	};

	const handleAnswered = async (answerIndex: number) => {
		const response = await vote({ voteUrl, id, answerIndex });
		if (handleSignOnRedirect(response)) {
			return;
		}
		if (response.status === 200) {
			setAnswersState((prevState) => {
				const nextState = [...prevState];
				nextState[answerIndex].votes = prevState[answerIndex].votes + 1;
				return nextState;
			});
			setAnswered(answerIndex.toString());
		} else if (response.status === 403) {
			// Forbidden - Internal users are authorized to view the poll results, but their votes are disregarded
			setAnswered(answerIndex.toString());
		} else if (response.status === NO_COOKIES_ACCEPTED_STATUS_CODE) {
			createCookieToaster(translationMap.cookieToaster);
			console.error(NO_COOKIES_ACCEPTED_ERROR_MSG);
		} else {
			console.error(response);
		}
	};

	const hydrated = useHydrated();
	const showResults = !!answered || expired;
	const isStatic = (!hydrated || recentlyLoaded) && showResults;

	const questionElementId = id + '-question-element';

	return (
		<>
			<SignInDialog ref={signInDialogRef} t={translationMap.signInDialog} />
			<Style
				className={joinString(['blk-ac-poll-container', theme.toLowerCase()])}
			>
				<div
					id={id}
					className={joinString([
						'blk-ac-poll',
						theme.toLowerCase(),
						isStatic ? 'static' : '',
					])}
				>
					<div className="poll-main-content">
						<div className="question-container">
							<Eyebrow className="large" icon="" tag={eyebrow} />
							<legend id={questionElementId}>{question}</legend>
						</div>
						<div className="poll-interactive-area-container">
							<div className="poll-interactive-area">
								<PollAnswers
									questionElementId={questionElementId}
									answers={answersState}
									answered={answered}
									showResults={showResults}
									votes={votes}
									handleAnswered={handleAnswerClicked}
									fullWidthAnimation={fullWidthAnimation}
								/>
								<div className="poll-context">
									<ul>
										{showResults && (
											<li>
												{votes}{' '}
												{votes === 1 ? translations.vote : translations.votes}
											</li>
										)}
										{expired ? (
											<li>{translations.pollClosed}</li>
										) : (
											<li>
												{expiry} {translations.left}
											</li>
										)}
										{showResults && !!pastPollResults && (
											<li>{translations.viewPastResults}</li>
										)}
									</ul>
								</div>
								{showResults && ctaContainers?.length === 1 && (
									<PollCTAContainer ctas={ctaContainers} />
								)}
							</div>
							<Suspense>
								{!isSsr() && hydrated && (
									<PollIcon answered={showResults}></PollIcon>
								)}
							</Suspense>
						</div>
					</div>
					{showResults && ctaContainers && ctaContainers.length > 1 && (
						<PollCTAContainer
							answered={parseInt(answered || '0')}
							ctas={ctaContainers}
							yourVoteLabel={answered ? translations.yourVote : null}
						/>
					)}
				</div>
			</Style>
		</>
	);
};
